import React, { useState, useContext } from 'react';
import { View, KeyboardAvoidingView, Text, StyleSheet, Platform } from 'react-native';
import { MainColors } from '../../constants/Colors';
import FontSizes from '../../constants/FontSizes';
import MainButton from '../../components/Buttons/MainButton';
import LinkStyleButton from '../../components/Buttons/LinkStyleButton';
import AuthTextInput from './components/AuthTextInput';
import { AuthContext } from '../../context/AuthProvider';
import ActivityIndicator from '../../components/ActivityIndicator'
import AppLogo from './components/AppLogo';

const LoginScreen = ({ navigation }) => {
    const { errorMessage, isNewPasswordRequired } = useContext(AuthContext);
    const { isLoading } = useContext(AuthContext);

    return (
        <ActivityIndicator isLoading={isLoading}>
            <KeyboardAvoidingView
                style={styles.screen}
                enabled
                behavior={Platform.OS === "ios" ? "padding" : "height"}
            >
                <AppLogo />
                <View style={styles.contentContainer}>
                    {errorMessage ? (
                        <Text style={styles.errorMessage}>{errorMessage}</Text>
                    ) : (null)}
                    {!isNewPasswordRequired ?
                        <LoginForm navigation={navigation} /> :
                        <ActivatePasswordForm navigation={navigation} />
                    }
                </View>
            </KeyboardAvoidingView>
        </ActivityIndicator>
    )
}

const LoginForm = ({ navigation }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    const onForgotPasswordPressedHandler = () => {
        navigation.navigate('ForgotPasswordScreen');
    }

    const onActivateAccountPressedHandler = () => {
        navigation.navigate('ActivateScreen');
    }

    const { login } = useContext(AuthContext);

    const submitLogin = () => login(email, password)

    return (
        <>
            <AuthTextInput
                placeholder='Email'
                autoCompleteType="email"
                keyboardType="email-address"
                returnKeyType='done'
                accessibilityLabel="Email"
                accessibilityHint="Type in your email here"
                value={email}
                onChangeText={email => setEmail(email)}
            />
            <AuthTextInput
                placeholder='Password'
                autoCompleteType="password"
                returnKeyType='done'
                accessibilityLabel="Password"
                accessibilityHint="Type in your password here"
                value={password}
                secureTextEntry={true}
                onChangeText={password => setPassword(password)}
                onSubmitEditing={submitLogin}
            />
            <View style={styles.forgotPasswordContainer}>
                <LinkStyleButton
                    style={styles.forgotPasswordBtnText}
                    onPress={onForgotPasswordPressedHandler}
                    accessibilityLabel="Forgot password?"
                    accessibilityHint="Press this button if you forgot your password"
                    buttonText="Forgot password?"
                />
            </View>
            <MainButton
                style={{ marginBottom: 30 }}
                onPress={submitLogin}
                text={'Login'}
                accessibilityLabel="Log in"
                accessibilityHint="Press this button to login"
            />
            <View style={styles.activateAcctContainer}>
                <Text style={styles.newHereText}>New Here?</Text>
                <LinkStyleButton
                    style={styles.activateAcctText}
                    onPress={onActivateAccountPressedHandler}
                    accessibilityLabel="Activate account"
                    accessibilityHint="Press this button for information on how to activate your account"
                    buttonText="Activate Account"
                />
            </View>
        </>
    )
}

const ActivatePasswordForm = ({ navigation }) => {
    const { completeNewPassword } = useContext(AuthContext);
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');

    return (
        <>
            <Text style={{ fontSize: FontSizes.medium, marginBottom: 20, color: MainColors.darkGray, textAlign: 'center' }}>We're going to need a new password, and something to call you.</Text>
            <AuthTextInput
                placeholder='Name'
                autoCompleteType="name"
                keyboardType="default"
                returnKeyType='done'
                accessibilityLabel="Name"
                accessibilityHint="Type in your name here"
                value={name}
                onChangeText={name => setName(name)}
            />
            <AuthTextInput
                placeholder='New Password'
                autoCompleteType="password"
                returnKeyType='done'
                accessibilityLabel="New Password"
                accessibilityHint="Type in your new password here"
                value={password}
                secureTextEntry={true}
                onChangeText={password => setPassword(password)}
            />
            <MainButton
                onPress={() => completeNewPassword(password, name)}
                text={'Set Password & Log In'}
                accessibilityLabel="Save Password Log in"
                accessibilityHint="Press this button to set your new password"
            />
        </>
    )
}

export const screenOptions = () => {
    return {
        headerShown: false,
        headerTitle: 'Login'
    }
}

const styles = StyleSheet.create({
    screen: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'white',
        padding: 30
    },
    contentContainer: {
        minWidth: 335,
        maxWidth: 420
    },
    forgotPasswordContainer: {
        alignSelf: 'flex-end',
        marginTop: -14,
        marginBottom: 50
    },
    forgotPasswordBtnText: {
        fontSize: FontSizes.small
    },
    newHereText: {
        fontSize: FontSizes.medium,
        marginRight: 5
    },
    activateAcctContainer: {
        flexDirection: 'row',
        alignSelf: 'center',
        marginTop: 50
    },
    activateAcctText: {
        fontSize: FontSizes.medium
    },
    errorMessage: {
        textAlign: 'center',
        color: MainColors.danger,
        marginBottom: 10,
    }
});

export default LoginScreen;

